import { call, put, select, takeLatest, fork } from 'redux-saga/effects'
import { UserConstants } from '../../user/store/user.constants'
import { isOffline, isOnline } from '../../../utilities'
import { navigateToCrew } from '../../../utilities/navigation.util'
import { AuthenticationService } from '../../login/services/authentication.service'
import { getUserSelector } from '../../user/selectors'
import Logger from '../../../services/logger.service'
import { setAuthorization, setUserPassword, setPrivileges } from '../../user/store/user.actions'
import {
  handleApiError,
  setErrorMessage,
  showToast,
} from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { AuthenticationRequest, AuthenticationResponse } from '../../login/store/login.types'
import { setAuthenticationJwt } from '../../login/store/login.actions'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { ZollAPIService } from '../../../services/zollApi.service'
import { onClientDataImportSuccess, setClientDataError } from '../../clientData/store/clientData.actions'
import { ClientDataService } from '../../../services/clientData.service'
import { setClientData as setClientDataSaga } from '../../clientData/saga/setClientData.saga'
import { setGlobalData as setGlobalDataSaga } from '../../globalData/saga/setGlobalData.saga'
import { setFillablePdfData as setFillablePdfDataSaga } from '../../fillablePDFs/saga/setFillablePdfData.saga'
import { GlobalDataService } from '../../../services/globalData.service'
import { onGlobalDataImportSuccess } from '../../globalData/store/globalData.actions'
import { initiateAutoUpload } from '../../home/store/home.actions'
import { startNotificationPolling } from '../../notificationCenter/store/notificationCenter.actions'
import { setFillablePDFsImportError, onFillablePDFsImportSuccess } from '../../fillablePDFs/store/fillablePDFs.actions'
import { setIsHydrating } from '../../session/persist/store/sessionPersist.actions'
import { getSessionPersistSelector } from '../../session/persist/store/sessionPersist.selectors'
import { DeviceDetailsService } from '../../login/services/deviceDetails.service'
import { IDeviceDetails } from '../../login/store/login.types'
import { HttpCodes } from '../../../common/httpCodes.constants'

export function* setServiceAuthorization() {
  const { userName, serviceId, password, tempOktaAccessToken } = yield select(getUserSelector)
  const { isHydrating } = yield select(getSessionPersistSelector)
  const authenticationJwt: string | null = yield select(getAuthenticationJwtSelector)
  if (isOnline()) {
    try {
      let deviceDetails: IDeviceDetails | undefined
      try {
        const deviceDetailsService = new DeviceDetailsService()
        deviceDetails = yield call([deviceDetailsService, deviceDetailsService.getDeviceDetails])
      } catch {
        Logger.warn('setServiceAuthorization: Unable to collect device details, proceeding without')
      }

      const authenticationRequest: AuthenticationRequest = {
        serviceId: +serviceId,
        username: userName,
        password: password,
        accessToken: tempOktaAccessToken,
        ...(deviceDetails && { deviceDetails }),
      }

      let response: AuthenticationResponse | null = null
      if (!isHydrating) {
        const authenticationService = new AuthenticationService()
        response = yield call([authenticationService, authenticationService.doAuthentication], authenticationRequest)
      }

      if (response?.status === HttpCodes.SUCCESS_200 || isHydrating) {
        if (isHydrating && !authenticationJwt) {
          yield put(handleApiError(ZollAPIService.createInvalidAuthenticationResponse()))
          return
        }

        if (response) {
          AuthenticationService.setAuthStateMap(response?.data?.authState)
          yield put(setAuthorization(response?.data.authState))
          yield put(setAuthenticationJwt(response?.authenticationJwt))
          const privileges = (response.data?.user?.privileges ?? []).map((p: { name: string }) => p.name)
          yield put(setPrivileges(privileges))
        }

        if (!isHydrating) {
          navigateToCrew()
        }

        yield call(setClientDataSaga)
        yield call(setGlobalDataSaga)
        yield fork(setFillablePdfDataSaga)
        yield put(initiateAutoUpload())
        yield put(setUserPassword(''))
        yield put(startNotificationPolling())
      } else if (response?.status === HttpCodes.NOTVALID_403) {
        yield put(handleApiError(response))
      } else {
        yield put(handleApiError(response))
      }
    } catch (e: any) {
      yield put(handleApiError(e))
    } finally {
      if (isHydrating) {
        yield put(setIsHydrating(false))
      }
    }
  } else if (isOffline()) {
    let canProgress = true

    const clientDataService = new ClientDataService(serviceId)
    const isEmpty: boolean = yield call([clientDataService, clientDataService.isEmpty])
    if (!isEmpty) {
      yield put(onClientDataImportSuccess())
      yield put(setClientDataError(''))
      yield put(onFillablePDFsImportSuccess())
      yield put(setFillablePDFsImportError(''))
    } else {
      yield put(setClientDataError('Client data not does exist, Please try again when online'))
      canProgress = false
    }

    const globalDataService = new GlobalDataService()
    const isGlobalDataEmpty: boolean = yield call([globalDataService, globalDataService.isEmpty])
    if (!isGlobalDataEmpty) {
      yield put(onGlobalDataImportSuccess())
    } else {
      yield put(
        setErrorMessage(
          'Please login to the emsCharts NOW in an online state so that we can complete the download of your configurations.'
        )
      )
      yield put(showToast(true))
      canProgress = false
    }

    if (canProgress && !isHydrating) {
      navigateToCrew()
    }
    if (isHydrating) {
      yield put(setIsHydrating(false))
    }
  }
}

export function* setServiceAuthorizationSaga() {
  yield takeLatest([UserConstants.SET_SERVICE], setServiceAuthorization)
}
